import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import TeacherEventDetailView from './TeacherEventDetailView';
import './TeacherEventsView.css';

function TeacherEventsView({ teacherData }) {
  const [events, setEvents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState('upcoming');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadClasses = useCallback(async () => {
    if (!teacherData?.uid) return;
    console.log('🔥 TeacherEvents: Loading classes for teacher', teacherData.uid);
    try {
      const classesQuery = query(
        collection(db, 'classes'),
        where('teacherId', '==', teacherData.uid)
      );
      const snapshot = await getDocs(classesQuery);
      const classesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      console.log('🔥 TeacherEvents: Classes loaded', classesData.length, classesData);
      setClasses(classesData);
    } catch (error) {
      console.error('🔥 TeacherEvents: Error loading classes:', error);
    }
  }, [teacherData]);

  const loadEvents = useCallback(async () => {
    console.log('🔥 TeacherEvents: Loading events');
    try {
      const eventsQuery = query(collection(db, 'events'), where('isActive', '==', true));
      const snapshot = await getDocs(eventsQuery);
      const allEvents = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      console.log('🔥 TeacherEvents: All events loaded', allEvents.length, allEvents);

      const teacherClassIds = classes.map(c => c.id);
      console.log('🔥 TeacherEvents: Teacher class IDs', teacherClassIds);
      const filteredEvents = allEvents.filter(event => {
        if (!event.participatingClassIds) return false;
        return event.participatingClassIds.some(classId => teacherClassIds.includes(classId));
      });
      console.log('🔥 TeacherEvents: Filtered events for teacher', filteredEvents.length, filteredEvents);

      setEvents(filteredEvents);
    } catch (error) {
      console.error('🔥 TeacherEvents: Error loading events:', error);
    } finally {
      setLoading(false);
    }
  }, [classes]);

  useEffect(() => {
    loadClasses();
  }, [loadClasses]);

  useEffect(() => {
    if (classes.length > 0) {
      loadEvents();
    }
  }, [classes, loadEvents]);

  const getFilteredEvents = () => {
    const now = new Date();
    switch (selectedFilter) {
      case 'upcoming':
        return events.filter(e => {
          const eventDate = e.date?.toDate ? e.date.toDate() : new Date(e.date);
          return eventDate >= now;
        }).sort((a, b) => {
          const dateA = a.date?.toDate ? a.date.toDate() : new Date(a.date);
          const dateB = b.date?.toDate ? b.date.toDate() : new Date(b.date);
          return dateA - dateB;
        });
      case 'past':
        return events.filter(e => {
          const eventDate = e.date?.toDate ? e.date.toDate() : new Date(e.date);
          return eventDate < now;
        }).sort((a, b) => {
          const dateA = a.date?.toDate ? a.date.toDate() : new Date(a.date);
          const dateB = b.date?.toDate ? b.date.toDate() : new Date(b.date);
          return dateB - dateA;
        });
      default:
        return events;
    }
  };

  const getEventIcon = (eventType) => {
    const icons = {
      'Marathon': '🏃',
      'Walkathon': '🚶',
      'Fun Run': '🏃',
      'Track and Field': '🏟️',
      'Field Trip': '🚌',
      'Assembly': '👥',
      'Sporting Event': '⚽',
      'Walk': '🚶',
      'Other': '📅'
    };
    return icons[eventType] || '📅';
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return 'N/A';
    if (dateValue.toDate) return dateValue.toDate().toLocaleDateString();
    return new Date(dateValue).toLocaleDateString();
  };

  const filteredEvents = getFilteredEvents();

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  // Full-screen event detail page (replaces the old modal)
  if (selectedEvent) {
    return (
      <TeacherEventDetailView
        event={selectedEvent}
        onClose={() => setSelectedEvent(null)}
      />
    );
  }

  return (
    <div className="teacher-events-view">
      <h2>Events</h2>

      <div className="filter-bar">
        <button
          className={`filter-button ${selectedFilter === 'upcoming' ? 'active' : ''}`}
          onClick={() => setSelectedFilter('upcoming')}
        >
          Upcoming
        </button>
        <button
          className={`filter-button ${selectedFilter === 'past' ? 'active' : ''}`}
          onClick={() => setSelectedFilter('past')}
        >
          Past
        </button>
      </div>

      {filteredEvents.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📅</div>
          <h3>No {selectedFilter} events</h3>
          <p>Events for your classes will appear here</p>
        </div>
      ) : (
        <div className="events-list">
          {filteredEvents.map(event => (
            <div
              key={event.id}
              className="event-card"
              onClick={() => setSelectedEvent(event)}
            >
              <div className="event-icon">{getEventIcon(event.eventType)}</div>
              <div className="event-info">
                <h4>{event.name}</h4>
                <p className="event-type">{event.eventType}</p>
                <p className="event-date">{formatDate(event.date)}</p>
                {event.description && (
                  <p className="event-description">{event.description}</p>
                )}
              </div>
              <div className="event-arrow">→</div>
            </div>
          ))}
        </div>
      )}

    </div>
  );
}

export default TeacherEventsView;
